import { FormSubmitButton, FormProvider } from '@mfw/forms';
import { PageLoader, useIntl } from '@mfw/components';
import Box from '@mui/material/Box';
import React from 'react';

import type { ApiQuestionnaireSubmission } from '../../../../../schemas/questionnaireSubmissions.schema';

import {
  useQuestionnaireSubmissionForm,
  QuestionField,
} from '../../../../../allFeatures/QuestionnaireSubmissionForm';
import {
  isBlockingPendingQuestionnaire,
  usePendingQuestionnaireSubmissions,
} from '../../../../../hooks/questionnaireSubmissions';
import { PendingQuestionnairesError } from '../PendingQuestionnairesStep/PendingQuestionnairesStep';
import { useClientOnboardingDialogContext } from '../../ClientOnboardingDialog';
import OnboardingHeader from '../../../OnboardingHeader';
import generalMessages from '../../../../../messages';

const QuestionnaireSubmissionDialogForm = ({
  submission,
}: {
  submission: ApiQuestionnaireSubmission;
}) => {
  const { formatMessage } = useIntl();
  const { handleNext, methods, questions } =
    useQuestionnaireSubmissionForm(submission);

  return (
    <FormProvider onSubmit={handleNext} {...methods}>
      <Box
        sx={{
          backgroundColor: 'background.paper',
          position: 'sticky',
          px: 3,
          py: 2,
          top: 0,
          zIndex: 2,
        }}
      >
        <OnboardingHeader
          description={
            submission.questionnaire.description ? (
              <p>{submission.questionnaire.description}</p>
            ) : undefined
          }
          title={submission.questionnaire.title}
        />
      </Box>
      <Box px={3}>
        {questions.map((question) => (
          <QuestionField question={question} key={question.id} />
        ))}
      </Box>
      <Box
        sx={{
          backgroundColor: 'background.paper',
          bottom: 0,
          display: 'flex',
          gap: 1,
          justifyContent: 'flex-end',
          position: 'sticky',
          px: 2,
          py: 1.5,
          zIndex: 1,
        }}
      >
        <FormSubmitButton
          data-testid="onboarding-next"
          variant="contained"
          size="large"
        >
          {formatMessage(generalMessages.next)}
        </FormSubmitButton>
      </Box>
    </FormProvider>
  );
};

export const PendingQuestionnairesDialogStep = () => {
  const { onFinish } = useClientOnboardingDialogContext();
  const {
    data: submissions,
    isError,
    isLoading,
    refetch,
  } = usePendingQuestionnaireSubmissions();

  const blockingSubmissions = submissions?.filter(
    isBlockingPendingQuestionnaire,
  );
  const submission = blockingSubmissions?.[0];

  React.useEffect(() => {
    if (!isLoading && blockingSubmissions && blockingSubmissions.length === 0) {
      onFinish?.();
    }
  }, [isLoading, blockingSubmissions, onFinish]);

  // See `PendingQuestionnairesStep` — the dialog would otherwise flash empty.
  if (isLoading) return <PageLoader />;
  // The host dialog has no close button, so a failed fetch with no branch
  // here left an empty, non-dismissible dialog until a full reload.
  if (isError) return <PendingQuestionnairesError onRetry={() => refetch()} />;
  if (!submission) return null;

  return (
    <QuestionnaireSubmissionDialogForm
      submission={submission}
      key={submission.id}
    />
  );
};
